/**
 * Shared RFQ section model — the canonical list of RFQ sections, their sourcing
 * modes, and the incoterm list. Used by the routed RFQ creator page (/rfq/new):
 * its standalone entry (standaloneSections) and its in-card "Create RFQ" pinned
 * entry (initialSections) share ONE definition (DRY).
 *
 * Mirrors SECTION_TYPES in the orchestrator (sectionForms.js). Ordered the way an
 * agent reads a lane, door-to-door.
 */

export const SECTIONS = [
  { key: 'PICKUP', label: 'Pickup (drayage)' },
  { key: 'ORIGIN', label: 'Origin charges' },
  { key: 'FREIGHT', label: 'Freight' },
  { key: 'DESTINATION', label: 'Destination charges' },
  { key: 'DELIVERY', label: 'Delivery (drayage)' },
  { key: 'CUSTOMS', label: 'Customs' },
  { key: 'WAREHOUSE', label: 'Warehouse' },
  { key: 'INSURANCE', label: 'Insurance (estimate)' },
];

// Sourcing modes per section (the partial-RFQ picker, used by the in-card modal).
export const SECTION_MODES = [
  { value: 'VENDOR_RFQ', label: 'Request an RFQ' },
  { value: 'PINNED_RATE', label: 'Pin this rate' },
  { value: 'INSTANT_ESTIMATE', label: 'Instant estimate' },
];

// Door-leg → port-charge coupling (mockup rule, prototype Contract view): ticking
// a drayage leg auto-ticks AND locks its port-charge section — a pickup drayage
// always lands at the origin port (origin charges), a delivery always starts at
// the destination port (destination charges). The reverse (LOCKED_BY) tells the
// selector which checkbox to disable while its driver leg is checked.
export const SECTION_LOCKS = { PICKUP: 'ORIGIN', DELIVERY: 'DESTINATION' };
export const LOCKED_BY = Object.fromEntries(
  Object.entries(SECTION_LOCKS).map(([driver, locked]) => [locked, driver])
);

// Short read-only note shown under a checked section that has no extra inputs —
// it still "reveals a card" (mockup ①③④), it just explains what vendors quote.
export const SECTION_NOTES = {
  ORIGIN: 'Origin-port charges (THC, doc fee, etc.) — vendors quote against the lane above. No extra input needed here.',
  FREIGHT: 'The main port-to-port leg — driven entirely by the lane (ports, containers, mode) captured above.',
  DESTINATION: 'Destination-port charges (THC, ISF, doc fee, etc.) — vendors quote against the lane above.',
};

// Card-input vocab for the WAREHOUSE detail section (mockup ⑦). The CUSTOMS and
// INSURANCE vocab moved into their own connected cards (RfqCustomsCard /
// RfqInsuranceCard) when those sections gained live Duty&Tax / premium estimates.
export const WAREHOUSE_SERVICE_TYPES = ['STORAGE', 'PICK_PACK', 'CROSS_DOCK', 'LABELING', 'OTHER'];

export function emptyCommodityLine() {
  return { description: '', hsCode: '', qty: '', declaredValue: '' };
}

// Stable per-row id so React keeps each warehouse-service row's inputs bound to its
// own data after a middle row is removed (index keys reuse the wrong DOM node).
// A session-local counter — no Date/random needed; warehousePayload() ignores _key.
let _wsSeq = 0;
export function emptyWarehouseService() {
  _wsSeq += 1;
  return { _key: `ws-${_wsSeq}`, serviceType: 'STORAGE', units: '', days: '', startDate: '', notes: '' };
}

// Fresh card state for the three detail sections. Each starts with one editable
// row so the revealed card is never empty.
// CUSTOMS card state: commodity/duty lines (one per HS line — Description / HS /
// Quantity / Value, the booking DutyAndTax fields) + the customs-broker choice
// (Prime Freight nominates +$75, or the customer's own broker).
export function freshCustoms() {
  return { lines: [emptyCommodityLine()], broker: 'primeFreight', brokerName: '' };
}

export function freshWarehouse() {
  return { services: [emptyWarehouseService()] };
}

// One insured commodity for the cargo-insurance quote (mirrors the booking
// CargoInsurance commodityArr row: category / description / value).
export function emptyInsuranceCommodity() {
  return { category: undefined, description: '', value: '' };
}

// Default ETD for the insurance quote — today + 7 days (a standalone RFQ has no
// selected sailing yet; Neil: insured = logged-in user + ETD default).
export function defaultEtd() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
}

export function freshInsurance() {
  return { commodities: [emptyInsuranceCommodity()], etd: defaultEtd() };
}

/**
 * The insured commodity rows that count toward the insurance quote — a positive
 * value AND some identity (description or category). Shared by RfqInsuranceCard
 * (the estimate's premium base) and buildRfqPayload (the persisted declared value)
 * so the displayed/estimated base and the saved insuranceDeclaredValue use one
 * identical predicate and can never disagree.
 */
export function insuredCommodities(commodities = []) {
  return (Array.isArray(commodities) ? commodities : []).filter(
    (c) => Number(c.value) > 0 && (c.description || c.category)
  );
}

/**
 * Build the commodities[] payload for C. When CUSTOMS is requested and its card
 * has real lines, the detailed customs declaration (per-HS-code rows) supersedes
 * the single top-level cargo description; otherwise fall back to that one line.
 * Empty/blank rows are dropped; numeric fields are coerced and omitted when 0.
 */
export function commoditiesPayload({ commodity, customs, customsRequested } = {}) {
  if (customsRequested && customs && Array.isArray(customs.lines)) {
    const lines = customs.lines
      .filter((l) => (l.description && l.description.trim()) || (l.hsCode && l.hsCode.trim()))
      .map((l) => {
        const out = { description: (l.description || '').trim() };
        if (l.hsCode && l.hsCode.trim()) out.hsCode = l.hsCode.trim();
        const qty = Number(l.qty);
        if (Number.isFinite(qty) && qty > 0) out.qty = qty;
        const dv = Number(l.declaredValue);
        if (Number.isFinite(dv) && dv > 0) out.declaredValue = dv;
        return out;
      });
    if (lines.length) return lines;
  }
  return commodity && commodity.trim() ? [{ description: commodity.trim() }] : [];
}

/**
 * Build the warehouseServices[] payload for C (one row per RFQWarehouseService).
 * Drops rows without a service type; coerces units/days and omits them when 0.
 */
export function warehousePayload(warehouse) {
  if (!warehouse || !Array.isArray(warehouse.services)) return [];
  return warehouse.services
    .filter((s) => s && s.serviceType)
    .map((s) => {
      const out = { serviceType: s.serviceType };
      const units = Number(s.units);
      if (Number.isFinite(units) && units > 0) out.units = units;
      const days = Number(s.days);
      if (Number.isFinite(days) && days > 0) out.days = days;
      if (s.startDate) out.startDate = s.startDate;
      if (s.notes && s.notes.trim()) out.notes = s.notes.trim();
      return out;
    });
}

export const INCOTERMS = ['EXW', 'FCA', 'FAS', 'FOB', 'CFR', 'CIF', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP'];

// Core lane an ocean/air offer always covers — pre-checked so the common case
// (full RFQ for the whole lane) is one click.
export const DEFAULT_INCLUDED = ['ORIGIN', 'FREIGHT', 'DESTINATION'];

/**
 * Section state for the in-card "Create RFQ" entry (RfqCardButton -> /rfq/new):
 * pins the card's freight rate (PINNED_RATE) and force-includes the sections the
 * card couldn't price (routed to vendors).
 */
export function initialSections({ pinFreight = false, preselectSections = [], freightIsEstimate = false } = {}) {
  const out = {};
  for (const s of SECTIONS) {
    let include = DEFAULT_INCLUDED.includes(s.key);
    // INSURANCE has no vendor form — default it to estimate.
    let mode = s.key === 'INSURANCE' ? 'INSTANT_ESTIMATE' : 'VENDOR_RFQ';
    // Opened from a card whose checked section can't be instantly priced:
    // force that section in and route it to vendors.
    if (preselectSections.includes(s.key)) {
      include = true;
      mode = 'VENDOR_RFQ';
    }
    // The card has a real freight rate — pin it rather than re-bid it. BUT an
    // ESTIMATE card's freight is not firm, so it stays RFQ'd (estimated freight
    // is RFQ-only); the estimate is still carried as a reference on the RFQ.
    if (pinFreight && s.key === 'FREIGHT') mode = freightIsEstimate ? 'VENDOR_RFQ' : 'PINNED_RATE';
    out[s.key] = { include, mode };
  }
  return out;
}

/**
 * Section state for the STANDALONE creator page: there is no card rate to pin and
 * nothing to estimate against, so every included section is a VENDOR_RFQ. (The
 * NA-only pickup/delivery instant estimate is layered on separately, per-section,
 * by the creator page — it is the sole estimate allowed in standalone mode.)
 */
export function standaloneSections({ included = DEFAULT_INCLUDED } = {}) {
  const out = {};
  for (const s of SECTIONS) {
    out[s.key] = { include: included.includes(s.key), mode: 'VENDOR_RFQ' };
  }
  return out;
}

/**
 * FCL container sizes the search offers. `key` matches the ratesearch counter
 * shape (state.code_config.fclCounters: _20stCounter / _40stCounter /
 * _40hqCounter / _45hqCounter) so the creator page can prefill its quantity
 * picker straight from the search. `size` maps to the vendor-portal's container
 * vocabulary (Prime-freight-portal VendorRfq/components/lane.js → CONTAINER_SIZES
 * = ["20","40","40HQ","45","LCL"]) so the lane.containers we post is the exact
 * shape B's normalizeContainers() reads back (cross-repo contract).
 */
export const FCL_CONTAINERS = [
  { key: '_20stCounter', label: '20 Standard', size: '20' },
  { key: '_40stCounter', label: '40 Standard', size: '40' },
  { key: '_40hqCounter', label: '40 High Cube', size: '40HQ' },
  { key: '_45hqCounter', label: '45 High Cube', size: '45' },
];

/**
 * Seed the creator-page quantity picker from the search's fclCounters, keeping
 * only the four known sizes (each defaulting to 0). Returns a fresh object so
 * editing the RFQ's containers never mutates the search sidebar's Redux state.
 */
export function containerCountsFromSearch(fclCounters = {}) {
  const out = {};
  for (const c of FCL_CONTAINERS) {
    const n = Number(fclCounters && fclCounters[c.key]);
    out[c.key] = Number.isFinite(n) && n > 0 ? n : 0;
  }
  return out;
}

/**
 * Build the lane.containers payload [{ size, qty }] (B's shape) from the picker
 * counts — only sizes with qty > 0. Empty array when nothing is selected.
 */
export function containersPayload(counts = {}) {
  const out = [];
  for (const c of FCL_CONTAINERS) {
    const qty = Number(counts && counts[c.key]) || 0;
    if (qty > 0) out.push({ size: c.size, qty });
  }
  return out;
}
